import type { BBox, MultiPolygon, PixelPoint, Pos, Projection } from "./types.ts";

/**
 * 只取主陸塊（coordinates[0]）的 bbox。資料管線保證最大地塊排在第一個，併入的離島接在其後；
 * 若把併入的離島也算進來，挪威會因為南大西洋的布威島而得到 -54~81 度的假 bbox，
 * 容許誤差與初始視野都會被拉壞。此處與 centroid 取最大塊的規則保持一致。
 *
 * 跨換日線的國家（俄羅斯、斐濟、紐西蘭…）若直接取 min/max 會得到橫跨整個地球的假 bbox，
 * 故改用「負經度 +360」的平移表示法，此時 maxLng 會大於 180（unwrapped 座標）。
 * 判準必須是「naive 跨距 > 180 度」——單看哪一種較窄的話，經度全為負的國家（如冰島）
 * 兩種表示法等寬，會因浮點誤差誤選平移版而被丟到地圖另一端。
 */
export function bboxOfPrimaryPolygon(geometry: MultiPolygon): BBox {
  const ring = geometry.coordinates[0]?.[0];
  if (!ring?.length) throw new Error("MultiPolygon 沒有任何座標");

  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of ring) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  if (maxLng - minLng <= 180) return [minLng, minLat, maxLng, maxLat];

  let shiftedMin = Infinity, shiftedMax = -Infinity;
  for (const [lng] of ring) {
    const s = lng < 0 ? lng + 360 : lng;
    if (s < shiftedMin) shiftedMin = s;
    if (s > shiftedMax) shiftedMax = s;
  }
  return shiftedMax - shiftedMin < maxLng - minLng
    ? [shiftedMin, minLat, shiftedMax, maxLat]
    : [minLng, minLat, maxLng, maxLat];
}

/** bbox 採用平移表示法時，畫面繪製也要跟著平移，否則拼圖片會橫跨螢幕變成一條爛痕 */
export function unwrapsAntimeridian(bbox: BBox): boolean {
  return bbox[2] > 180;
}

/**
 * 「整組」拼圖片是否要改用平移表示法。單片由 bboxOfPrimaryPolygon 處理，但一組仍可能被
 * 換日線拆散：大洋洲的斐濟（177）與薩摩亞（-172）分屬兩側，亞洲從普通難度起俄羅斯換日線
 * 東側那片會單獨成片，naive 取 min/max 就得到 350~360 度的假跨距，開局視野縮成整個地球。
 *
 * 判準是「平移後塞得進半個地球」而非單純取較窄者：世界地圖極難的平移跨距 359.7 度，
 * 只比 naive 的 360 度窄 0.3，取較窄者會把整張世界地圖從格林威治劈成兩半。
 */
export function usesShiftedFrame(bboxes: BBox[]): boolean {
  const HALF_GLOBE = 180;
  let min = Infinity, max = -Infinity, shiftedMin = Infinity, shiftedMax = -Infinity;
  for (const bbox of bboxes) {
    for (const lng of [bbox[0], bbox[2]]) {
      if (lng < min) min = lng;
      if (lng > max) max = lng;
      const s = lng < 0 ? lng + 360 : lng;
      if (s < shiftedMin) shiftedMin = s;
      if (s > shiftedMax) shiftedMax = s;
    }
  }
  const shiftedSpan = shiftedMax - shiftedMin;
  return shiftedSpan <= HALF_GLOBE && shiftedSpan < max - min;
}

export function pixelDistance(a: PixelPoint, b: PixelPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * 取 bbox 兩個對角在畫面上的像素距離。
 * 只投影對角點即可，因為等距圓柱與麥卡托都是軸向單調投影，bbox 角落投影後仍是角落。
 */
export function projectedBBoxDiagonalPx(bbox: BBox, projection: Projection): number {
  const [minX, minY, maxX, maxY] = bbox;
  const a = projection.toPixel([minX, minY] as Pos);
  const b = projection.toPixel([maxX, maxY] as Pos);
  return pixelDistance(a, b);
}

export function shuffle<T>(items: T[], rng: () => number): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
